import type { CellClassParams, ValueGetterParams, ColDef, ColGroupDef, ValueFormatterParams } from 'ag-grid-community';
import {
  formatColumnDate,
  dateFilterParams,
  headerInfoTooltipDef,
  formatLongPrice,
  getPureNumber,
  getValueBgClass,
} from '../lib/utils';

const defaultFilterParams = {
  newRowsAction: 'keep',
};

const EPSFilter = [
  {
    label: '0 to 5',
    rules: (value: number) => {
      return value >= 0 && value <= 5;
    },
  },
  {
    label: '5 to 10',
    rules: (value: number) => {
      return value >= 5 && value <= 10;
    },
  },
  {
    label: '10 to 15',
    rules: (value: number) => {
      return value >= 10 && value <= 15;
    },
  },
  {
    label: '15 to 20',
    rules: (value: number) => {
      return value >= 15 && value <= 20;
    },
  },
  {
    label: '20+',
    rules: (value: number) => {
      return value >= 20;
    },
  },
];

const REVFilter = [
  {
    label: '$0 to $100M',
    rules: (value: number) => {
      return value >= 0 && value <= 100000000;
    },
  },
  {
    label: '$100M to $500M',
    rules: (value: number) => {
      return value >= 100000000 && value <= 500000000;
    },
  },
  {
    label: '$500M to $1B',
    rules: (value: number) => {
      return value >= 250000000 && value <= 1000000000;
    },
  },
  {
    label: '$1B to $10B',
    rules: (value: number) => {
      return value >= 1000000000 && value <= 10000000000;
    },
  },
  {
    label: '$10B+',
    rules: (value: number) => {
      return value >= 10000000000;
    },
  },
];

const colDef: (ColDef | ColGroupDef)[] = [
  {
    cellClass: 'ticker-cell',
    field: 'date',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    headerName: 'Date Announced',
    initialWidth: 120,
    minWidth: 110,
    valueFormatter: ({ data }: ValueFormatterParams) => formatColumnDate(data.date),
    valueGetter: ({ data }: ValueGetterParams) => {
      if (!data.date) return null;

      return data.date;
    },
    ...headerInfoTooltipDef('Date Guidance is announced to the public'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: 'tickerWidgetCell',
    field: 'ticker',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 80,
    minWidth: 70,
    ...headerInfoTooltipDef('Symbol associated with the company who the Guidance is given for'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ data }: any) => {
      return (
        <div className="company-name-cell">
          <span>{data.name}</span>
          {data.logo && <img alt="" className="bz-ag-table__company-image" loading="lazy" src={data.logo}></img>}
        </div>
      );
    },
    cellRendererParams: {
      hideOnInitialTicker: true,
    },
    field: 'name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Company Name',
    initialWidth: 200,
    minWidth: 180,
    ...headerInfoTooltipDef('Full name of company associated to ticker'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'period',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Period',
    initialWidth: 95,
    minWidth: 90,
    ...headerInfoTooltipDef('Time Frame being reported'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'period_year',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Period Year',
    initialWidth: 110,
    minWidth: 100,
    ...headerInfoTooltipDef('Year of guidance issuance'),
  },
  {
    cellClass: ({ value }: CellClassParams) => getValueBgClass(value),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'eps_guidance_min',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: EPSFilter,
    },
    headerName: 'Min EPS',
    initialWidth: 100,
    minWidth: 95,
    valueFormatter: ({ value }: ValueFormatterParams) => formatLongPrice(value),
    valueGetter: ({ data }: ValueGetterParams) => getPureNumber(data.eps_guidance_min),
    ...headerInfoTooltipDef('Low end of EPS Estimate'),
  },
  {
    cellClass: ({ value }: CellClassParams) => getValueBgClass(value),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'eps_guidance_max',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: EPSFilter,
    },
    headerName: 'Max EPS',
    initialWidth: 100,
    minWidth: 95,
    valueFormatter: ({ value }: ValueFormatterParams) => formatLongPrice(value),
    valueGetter: ({ data }: ValueGetterParams) => getPureNumber(data.eps_guidance_max),
    ...headerInfoTooltipDef('High end of EPS estimate'),
  },
  {
    cellClass: ({ value }: CellClassParams) => getValueBgClass(value),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'eps_guidance_est',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: EPSFilter,
    },
    headerName: 'Estimate',
    initialWidth: 100,
    minWidth: 95,
    valueFormatter: ({ value }: ValueFormatterParams) => formatLongPrice(value),
    valueGetter: ({ data }: ValueGetterParams) => getPureNumber(data.eps_guidance_est),
    ...headerInfoTooltipDef('Estimated EPS number'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'revenue_guidance_min',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: REVFilter,
    },
    headerName: 'Min Rev',
    initialWidth: 100,
    minWidth: 95,
    valueFormatter: ({ value }: ValueFormatterParams) => formatLongPrice(value),
    valueGetter: ({ data }: ValueGetterParams) => getPureNumber(data.revenue_guidance_min),
    ...headerInfoTooltipDef('Low end of revenue estimate'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'revenue_guidance_max',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: REVFilter,
    },
    headerName: 'Max Rev',
    initialWidth: 100,
    minWidth: 95,
    valueFormatter: ({ value }: ValueFormatterParams) => formatLongPrice(value),
    valueGetter: ({ data }: ValueGetterParams) => getPureNumber(data.revenue_guidance_max),
    ...headerInfoTooltipDef('High end of revenue estimate'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'revenue_guidance_est',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: REVFilter,
    },
    headerName: 'Revenue Estimate',
    initialWidth: 150,
    minWidth: 140,
    valueFormatter: ({ value }: ValueFormatterParams) => formatLongPrice(value),
    valueGetter: ({ data }: ValueGetterParams) => getPureNumber(data.revenue_guidance_est),
    ...headerInfoTooltipDef('Number depicting the Estimated Revenue'),
  },

  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 70,
    minWidth: 70,
    suppressAutoSize: true,
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
  },
];

export default colDef;
